import currencyFormat from "../../functions/currency";
import ServiceFactory from "../../service";
import account from "../../data/account";

/*
 * Shared helpers for the Territory & Rep Intelligence surface
 * (overview + market detail). Data contract: PlunkDocs
 * specs/TERRITORY_REP_INTELLIGENCE_UI.md — AnalyticsService /v1/sales/*.
 */

// The shared Plunk range vocabulary (Territory spec §1) — the SAME picker set
// as Market Intelligence so the surfaces mirror. last_7_days is the rolling
// trailing week (distinct from calendar this_week).
export const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["last_7_days", "Last 7 Days"], ["this_week", "This Week"], ["last_week", "Last Week"], ["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["last_quarter", "Last Quarter"], ["this_year", "This Year"], ["last_year", "Last Year"], ["all_time", "All Time"]];

// Coerce before formatting: the service stack can serialize decimals as
// STRINGS, and string.toLocaleString is a no-op (and += concatenates).
// Not currencyFormat — it renders 0 as '-----', but a zero COUNT is a real
// value (e.g. Fairfield's Selling Reps = 0 until per-order rep ids exist).
// Title Case for names: feed rows arrive ALL-CAPS, web/tracker rows all-
// lowercase — CSS text-transform can't normalize both. Never apply to emails.
export function titleCase(v) {
  return String(v).toLowerCase().replace(/(^|[\s\-'.&(])([a-z])/g, (m, pre, c) => pre + c.toUpperCase())
  // Keep grade numerals (II, III, IV) and short vowel-less codes (SW, LF, RF) in capitals.
  .replace(/(^|[^A-Za-z])(i{1,3}|iv|vi{0,3}|ix|x)(?=$|[^A-Za-z])/gi, (m, pre, r) => pre + r.toUpperCase()).replace(/(^|[^A-Za-z])([b-df-hj-np-tv-xz]{2,3})(?=$|[^A-Za-z.])/gi, (m, pre, w) => pre + w.toUpperCase());
}
export function num(v) {
  return (Number(v) || 0).toLocaleString("en-US");
}

// Product titles from the feed carry CSV escape cruft — an inch-mark (86") gets
// quote-escaped through the pipeline into runs like """""""86""""""". Collapse
// any run of 2+ double-quotes to one, then strip the outer wrapping quotes/space,
// leaving the real inch-mark in place (86" Tapered Arm Sofa).
export function cleanTitle(v) {
  if (v == null) return v;
  const cleaned = String(v).replace(/"{2,}/g, '"').replace(/^["\s]+|["\s]+$/g, "").trim();
  return cleaned || String(v);
}

// SALES channel (who sells — the rep's channel, e.g. Commercial /
// Residential / Senior Living), distinct from lifecycle b2b/b2c (who buys).
// null / "unsegmented" = reps without a channel tag.
export function channelLabel(v) {
  if (!v || String(v).toLowerCase() === "unsegmented") return "Unsegmented";
  return titleCase(v);
}
export function usd(v) {
  const n = Number(v) || 0;
  if (!n) return "-----";
  // Always two decimals — toLocaleString alone drops trailing zeros
  // ($1,203.3 instead of $1,203.30).
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function aov(totalAmount, orders) {
  const t = Number(totalAmount) || 0;
  const o = Number(orders) || 0;
  return o > 0 ? usd(t / o) : "-----";
}
export function fetchSales(api) {
  const Service = ServiceFactory.getType(api, "product");
  const service = new Service();
  return service.getAsync();
}

// Trend bucket appropriate to the visible range.
// %Δ vs the prior window (?comparison=previous_period rows carry Prev*
// fields). Returns null when there's no meaningful prior value (prev<=0) or
// for all_time (no prior window exists — don't request comparison for it).
export function deltaOf(cur, prev) {
  const c = Number(cur) || 0;
  const p = Number(prev) || 0;
  if (p <= 0) return null;
  const pct = (c - p) / p * 100;
  const dir = pct > 0.05 ? "up" : pct < -0.05 ? "down" : "flat";
  const text = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs prev`;
  return {
    text,
    dir,
    pct
  };
}

// Elapsed fraction of an IN-PROGRESS period (0.05–1). Complete/relative ranges
// (last_month, custom, all_time, …) return 1. Mirrors the Stores "pace" read.
export function elapsedFraction(range) {
  const now = new Date();
  let f = 1;
  if (range === "today") f = (now.getHours() + 1) / 24;else if (range === "this_week") f = ((now.getDay() + 6) % 7 + 1) / 7;else if (range === "this_month") {
    const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    f = now.getDate() / days;
  } else if (range === "this_quarter") {
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1);
    const end = new Date(now.getFullYear(), q * 3 + 3, 1);
    f = (now - start) / (end - start);
  } else if (range === "this_year") {
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear() + 1, 0, 1);
    f = (now - start) / (end - start);
  }
  return Math.min(1, Math.max(0.05, f));
}

// Delta against the comparison window for the sales rails (/v1/sales/*, Sales cockpit, lifecycle funnel).
// Those APIs already pace the prior value: an in-progress range is compared with the same ELAPSED dates one
// period back (Oct 1-12 vs Sep 1-12; Service.Product ComparisonWindows), so the prior is NOT scaled again here.
// Scaling it a second time inflated every in-progress change (Ambella 10-12: Sales +159% and Dashboard +118%
// while the truth was -40%). The noise floor stays: a tiny prior makes the % explode on small counts.
export function deltaOfPaced(cur, prev, range) {
  const frac = elapsedFraction(range);
  const p = Number(prev) || 0;
  if (frac < 0.999 && p < 10) return null;
  return deltaOf(cur, p);
}

// For an API whose prior value is the WHOLE previous period (the contacts activity dashboard): scale the prior
// down to the elapsed fraction before comparing, so 7 days into a month doesn't read "-75% vs the full month".
export function deltaVsFullPrior(cur, prev, range) {
  const frac = elapsedFraction(range);
  const p = Number(prev) || 0;
  const scaled = p * frac;
  if (frac < 0.999 && (p < 10 || scaled < 1)) return null;
  const d = deltaOf(cur, scaled);
  if (d && frac < 0.999) d.text = d.text.replace("vs prev", "vs pace");
  return d;
}

// Comparison querystring fragment. `comp` uses the Activity-pages control
// vocabulary ('prev_period' | 'prev_year' — the shared ActivityComparisonMenu)
// mapped to the API tokens. all_time has no prior window — no comparison.
// `first` = this is the first query param.
export function apiComparison(comp) {
  return comp === "prev_year" ? "previous_year" : "previous_period";
}
export function comparisonQs(range, first, comp) {
  if (range === "all_time") return "";
  return `${first ? "?" : "&"}comparison=${apiComparison(comp)}`;
}

// Trend-chart prior-line overlay: a "Prev Revenue" dashed grey line when the
// comparison-populated trend rows carry prior values — the same prior-line
// treatment as the Activity pages' charts.
// Account lens (SALES_ACCOUNT_FILTER.md): exclude/isolate selected accounts by
// their sales_transaction.customer_id (= business_customer.account_number). The
// param key is the only difference between the two modes; empty = no-op.
export function accountLensKv(mode, ids) {
  if (!ids || !ids.length) return "";
  return `${mode === "isolate" ? "onlyCustomers" : "excludeCustomers"}=${encodeURIComponent(ids.join(","))}`;
}
const ACCT_LENS_KEY = "plk_territory_acct_lens";
export function readAcctLens(tenantId) {
  try {
    const all = JSON.parse(localStorage.getItem(ACCT_LENS_KEY) || "{}");
    const v = all[tenantId];
    if (v && Array.isArray(v.ids)) return {
      mode: v.mode === "isolate" ? "isolate" : "exclude",
      ids: v.ids.map(String)
    };
  } catch (e) {/* ignore */}
  return {
    mode: "exclude",
    ids: []
  };
}
export function writeAcctLens(tenantId, mode, ids) {
  try {
    const all = JSON.parse(localStorage.getItem(ACCT_LENS_KEY) || "{}");
    if (ids && ids.length) all[tenantId] = {
      mode,
      ids
    };else delete all[tenantId];
    localStorage.setItem(ACCT_LENS_KEY, JSON.stringify(all));
  } catch (e) {/* ignore */}
}
export const PREV_LINE_COLOR = "#9aa4b2";
export function hasPrevTrend(trend) {
  return (trend || []).some(t => (Number(t.prevTotalAmount) || 0) > 0);
}
export function bucketFor(range) {
  if (/today|yesterday|7_days|week|month/.test(range)) return "day";
  if (/quarter/.test(range)) return "week";
  return "month"; // years + all_time
}
export function trendLabel(period, bucket) {
  // period arrives as 'YYYY-MM-DD'
  const [y, m, d] = String(period).split("-").map(Number);
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (bucket === "month") return `${MONTHS[(m || 1) - 1]} '${String(y).slice(2)}`;
  return `${m}/${d}`;
}

// business_model per tenant (retail | manufacturing | both).
//
// Resolution order (SALES_FEATURES_REVIEW_2026-10-05 §2.1):
//   1. the boot tenantConfig the broker delivers at sign-in
//      (localStorage `plunkTenantConfig`, guarded to the signed-in tenant)
//      (`businessModel` rides on the registry config, no round-trip);
//   2. AccountService GET /v1/account/{id} → `businessModel`;
//   3. "both": show every vertical slot rather than guess wrong. The old
//      default was "manufacturing", which put Dealers / Territory in front
//      of a retail tenant whenever the account call failed. A one-time
//      console warning says the fallback was used.
// Localhost dev can pin it: localStorage.plunkDevBusinessModel = 'retail'.
const BUSINESS_MODELS = ["retail", "manufacturing", "both"];
const _bmCache = {};
let _bmWarned = false;
function bootBusinessModel(tenantId) {
  try {
    const raw = localStorage.getItem("plunkTenantConfig");
    const cfg = raw ? JSON.parse(raw) : null;
    if (!cfg) return null;
    if (tenantId && cfg.tenantId && String(cfg.tenantId) !== String(tenantId)) return null; // §215 guard: never another tenant's config
    const bm = String(cfg.businessModel || cfg.BusinessModel || "").toLowerCase();
    return BUSINESS_MODELS.includes(bm) ? bm : null;
  } catch (e) {
    return null;
  }
}
export async function resolveBusinessModel() {
  const tenantId = (account.get() || {}).tenantId;
  if (!tenantId) return "both";
  if (_bmCache[tenantId]) return _bmCache[tenantId];
  try {
    const dev = localStorage.getItem("plunkDevBusinessModel");
    if (dev && BUSINESS_MODELS.includes(String(dev).toLowerCase())) {
      _bmCache[tenantId] = String(dev).toLowerCase();
      return _bmCache[tenantId];
    }
  } catch (e) {/* no storage */}
  const boot = bootBusinessModel(tenantId);
  if (boot) {
    _bmCache[tenantId] = boot;
    return boot;
  }
  try {
    const rows = await fetchSales(`/account/${tenantId}`);
    const row = Array.isArray(rows) ? rows[0] : rows;
    const bm = String(row && (row.businessModel || row.BusinessModel) || "").toLowerCase();
    if (BUSINESS_MODELS.includes(bm)) {
      _bmCache[tenantId] = bm;
      return bm;
    }
  } catch (e) {/* fall through to the safe default */}
  if (!_bmWarned) {
    _bmWarned = true;
    try {
      console.warn("[plunk] businessModel not on the boot config or the account record; showing every sales surface (both).");
    } catch (e) {/* no console */}
  }
  _bmCache[tenantId] = "both";
  return "both";
}

// Inventory-planner gate (INVENTORY_UX.md): the feed self-selects the planable
// set, so "does this tenant have an inventory snapshot" IS the nav gate — no
// per-tenant feature seed. Probed once per tenant; 404 (inventory_not_available)
// or any error → hidden. LEE (all made-to-order, no feed) never lights up.
const _invCache = {};
export async function inventoryAvailable() {
  const tenantId = (account.get() || {}).tenantId;
  if (!tenantId) return false;
  if (tenantId in _invCache) return _invCache[tenantId];
  try {
    const r = await fetchSales("/inventory/summary");
    _invCache[tenantId] = !!(r && r.totals);
  } catch (e) {
    _invCache[tenantId] = false;
  }
  return _invCache[tenantId];
}

// US states + DC/PR — display names and the set of codes the GeoChart can
// render (dealer ship_to_region carries Canadian provinces etc. too; those
// stay in the tables but are excluded from the US map).
export const US_STATES = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  PR: "Puerto Rico"
};
export function stateName(code) {
  return US_STATES[String(code || "").toUpperCase()] || code;
}

/* ── custom date-range tokens (CUSTOM_DATE_RANGES.md) ──
 * `custom_YYYYMMDD_YYYYMMDD` — inclusive start/end, accepted everywhere a
 * {range} appears (Phase A query-time; Phase B stats families). Route-safe. */

export const isCustomRange = r => /^custom_\d{8}_\d{8}$/.test(String(r || ""));
export function parseCustomRange(r) {
  const m = String(r || "").match(/^custom_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})$/);
  if (!m) return null;
  return [new Date(+m[1], +m[2] - 1, +m[3]), new Date(+m[4], +m[5] - 1, +m[6])];
}
export function makeCustomRange(startISO, endISO) {
  // inputs are yyyy-mm-dd from <input type=date>
  return `custom_${startISO.replace(/-/g, "")}_${endISO.replace(/-/g, "")}`;
}
export function customRangeLabel(r) {
  const w = parseCustomRange(r);
  if (!w) return "Custom";
  const f = d => d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
  return `${f(w[0])} – ${f(w[1])}`;
}

/** Preset label or the custom window's dates. */
export function rangeLabelFor(range, presets) {
  if (isCustomRange(range)) return customRangeLabel(range);
  const hit = (presets || RANGES).find(([v]) => v === range);
  return hit ? hit[1] : range;
}

// Address basis for placing dealers (Territory & Reps redesign 2026-10-09):
// "bill" = head office (bill-to, the default for this page), "ship" = delivery
// location. Sent as ?basis= on territory calls; the backend falls back to the
// ship-to field when a dealer's bill-to is blank, and demand gaps always use ship-to.
export const BASIS_OPTIONS = [{
  value: "bill",
  label: "Bill-to"
}, {
  value: "ship",
  label: "Ship-to"
}];
export function readBasis(tenantId) {
  try {
    const v = localStorage.getItem(`plunk.territory.basis.${tenantId || ""}`);
    return v === "ship" ? "ship" : "bill";
  } catch (e) {
    return "bill";
  }
}
export function writeBasis(tenantId, v) {
  try {
    localStorage.setItem(`plunk.territory.basis.${tenantId || ""}`, v === "ship" ? "ship" : "bill");
  } catch (e) {/* per-viewer convenience only */}
}